import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import Panel from '../Panel';
import clip from 'text-clipper';
import { safeParseHtml } from '~/utils/safeParseHtml';
import { linkifyUrls } from '~/utils/linkifyUrls';
import NotesModal from '~/components/Common/NotesModal';

interface Note {
    id: number;
    created_date: string;
    created_by_name: string;
    note: string;
    tags?: string;
    assignee?: string;
    pinned_flag?: boolean;
    todo_flag?: boolean;
    completed_by_name?: string;
    completed_date?: string | null;
    deleted_date?: string | null;
}

interface Props {
    notes: Note[];
    setNotes: (notes: Note[] | ((prev: Note[]) => Note[])) => void;
}

const PinnedNotes: React.FC<Props> = ({ notes, setNotes }) => {
    const [notesModal, setNotesModal] = useState<{
        isOpen: boolean;
        note: Note | null;
    }>({ isOpen: false, note: null });

    const notesMarkup = notes.map((note: Note) => {
        const hasNotes = note.note.length > 0;
        const handleNotesClick = () => {
            if (hasNotes) {
                setNotesModal({
                    isOpen: true,
                    note: note,
                });
            }
        };

        return (
            <div className='py-[12px]' key={note.id}>
                <div className='flex justify-between'>
                    <div>{format(parseISO(note.created_date), 'MM/dd/yy')}</div>
                    <div className='flex gap-[10px]'>
                        <div>{note.created_by_name}</div>
                        <button>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='18'
                                height='18'
                                viewBox='0 0 18 18'
                                fill='none'
                            >
                                <path
                                    d='M12 9L13.5 10.5V12H9.75V16.5L9 17.25L8.25 16.5V12H4.5V10.5L6 9V3.75H5.25V2.25H12.75V3.75H12V9Z'
                                    fill='#F02D3A'
                                />
                            </svg>
                        </button>
                    </div>
                </div>
                <div
                    className='cursor-pointer bg-[#FDEAEB] p-[12px] transition-colors hover:opacity-80'
                    onClick={handleNotesClick}
                    title={hasNotes ? 'Click to view full notes' : ''}
                >
                    {safeParseHtml(linkifyUrls(clip(note.note, 140, { html: true, maxLines: 5 })))}
                </div>
                {note.assignee && (
                    <div className='text-[12px]/[16px] text-[#949599] mt-1'>
                        Assigned to: {note.assignee}
                    </div>
                )}
                {/* <div className='text-end text-[12px]/[16px] text-[#949599]'>TAGS</div> */}
            </div>
        );
    });

    return (
        <>
            <Panel>
                <div className='text-[22px]/[28px] font-bold'>Pinned Notes</div>

                {notesMarkup}
            </Panel>

            <NotesModal
                isOpen={notesModal.isOpen}
                note={notesModal.note}
                onClose={() => setNotesModal({ isOpen: false, note: null })}
                onPinToggle={(updatedNote: Note) => {
                    setNotes((prevNotes: Note[]) =>
                        prevNotes.map((n) =>
                            n.id === updatedNote.id ? updatedNote : n
                        )
                    );
                }}
                onDelete={(noteId: number) => {
                    setNotes((prevNotes: Note[]) =>
                        prevNotes.filter((n) => n.id !== noteId)
                    );
                }}
            />
        </>
    );
};

export default PinnedNotes;
